import useWindowSize from "@dana/Common/Hooks/useWindowSize";
import setDesktopFlyoutDrawerIsOpen from "@dana/Handlers/SetDesktopFlyoutDrawerIsOpen";
import setDesktopNavDrawerIsOpen from "@dana/Handlers/SetDesktopNavDrawerIsOpen";
import PanelMenu from "@dana/MobiusOverrides/PanelMenu/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@dana/MobiusOverrides/PanelMenu/PanelRow";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { iconMap } from "@dana/Overrides/Widgets/Header/MainNavigation";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationStateDana) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        navDrawerIsOpen: state.desktopManinNavigationState?.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.desktopManinNavigationState?.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        cart: getCurrentCartState(state).value,
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setDesktopNavDrawerIsOpen,
    setDesktopFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    rootElementSelector?: string;
    allowOpenParentLinks?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        css: css`
            &&.menu-trigger-button {
                color: ${themeColors.primary.richBlack};
                &:focus,
                &:hover {
                    color: ${themeColors.primary.white};
                }
            }

            &&&.menu-trigger-button.clicked.clicked {
                background-color: transparent;
                span {
                    color: ${themeColors.secondary.danaBrightBlue};
                }
            }
        `,
    },
    drawer: {
        size: 400,
        cssOverrides: {
            drawerContainer: css`
                &&.drawer {
                    display: flex;
                }
            `,

            scrim: css`
                top: var(--header-nav-height);
            `,
            drawerContent: css`
                margin-top: -2px;
                overflow-x: hidden;
                .drawer-next-button {
                    width: 16px;
                    height: 16px;
                    color: ${themeColors.primary.richBlack};
                    margin-left: auto;
                    svg {
                        width: 16px;
                        height: 16px;
                        path {
                            fill: ${themeColors.primary.richBlack};
                        }
                    }
                }
            `,
            drawerTitle: css`
                background: ${themeBackgrounds.lightGray};
                border-bottom: 1px solid ${themeBackgrounds.lightGray};
                [data-test-selector="closeDrawer"] {
                    min-hright: initial;
                }
            `,
            titleButton: css`
                background: ${themeBackgrounds.lightGray};
                padding: 0;
                align-items: center;
                border: none;
                span {
                    svg {
                        color: ${themeColors.primary.richBlack};
                    }
                }

                &:hover {
                    border: none;
                    background: transparent;
                }
            `,
            headlineTypography: css`
                margin: 0;
                font-size: ${fontSizes.medium};
                font-weight: ${fontWeights.medium};
                line-height: ${lineHeights.defaultLarge};
            `,
        },
    },

    drawerSectionWrapper: {
        css: css`
            background: ${themeColors.primary.white};
            &:first-child {
                margin-top: 0;
                padding-bottom: 10px;
                background: ${themeColors.primary.white};
            }
            &:last-child {
                margin-bottom: 5px;
            }
            margin-top: 20px;
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                text-align: left;
                > a,
                button {
                    height: 42px;
                }
            `,
            menu: css``,
            children: css`
                > .mobile-menu-header {
                    padding: 8px 16px;

                    .back-button {
                        padding: 0px;
                        span[class*="IconWrapper"] {
                            align-items: center;
                            color: ${themeColors.primary.richBlack};
                            font-size: ${fontSizes.medium};
                            line-height: ${lineHeights.medium};
                            font-weight: ${fontWeights.regular};
                        }

                        span:not([class*="ButtonIcon"]) {
                            color: ${themeColors.primary.richBlack};
                            margin-top: 2px;
                            font-size: ${fontSizes.medium};
                            line-height: ${lineHeights.medium};
                            font-weight: ${fontWeights.regular};
                        }
                    }

                    .close-button {
                        padding: 0px;
                        align-items: center;
                        > span {
                            align-items: center;
                            font-size: ${fontSizes.medium};
                            line-height: ${lineHeights.medium};
                            font-weight: ${fontWeights.regular};
                        }
                    }
                }
            `,
        },
        childTypographyProps: {
            transform: "capitalize",
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        transform: "capitalize",
        color: "black",
    },
    punchOutUserRowText: {
        transform: "capitalize",
        color: "black",
    },
    mainNavigationRow: {
        color: themeColors.primary.richBlack,
        css: css`
            width: 100%;
            display: flex;
            padding: 8px 16px;
            justify-content: start;
            background: ${themeColors.primary.white};
            gap: 24px;
            align-items: center;
            align-self: stretch;

            .drawer-next-button {
                width: 16px;
                height: 16px;
                color: ${themeColors.primary.richBlack};
                margin-left: auto;
                svg {
                    width: 16px;
                    height: 16px;
                    path {
                        fill: ${themeColors.primary.richBlack};
                    }
                }
            }
        `,
    },
    mainNavigationRowIcon: {
        src: "User",
        color: "black",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "white",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        transform: "capitalize",
        color: "black",
        weight: "normal",
    },
    logoLinks: {
        color: "common.accent",
        typographyProps: {
            ellipsis: true,
            transform: "capitalize",
        },
        css: css`
            margin: 2px;
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: capitalize;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    signOutRowText: {
        transform: "uppercase",
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer = ({
    links,
    displayVmiNavigation,
    vmiPageLinks,
    displayModeSwitch,
    currentMode,
    isCompactHeaderFlyout,
    showFlyoutPricingToggle,
    showFlyoutInventoryToggle,
    currentLocation,
    signInUrl,
    vmiDashboardPageUrl,
    homePageUrl,
    headerLinkListLinkFields,
    navDrawerIsOpen,
    flyoutDrawerIsOpen,
    cart,
    history,
    theme,
    displayPricingAndInventoryToggles,
    setInitialValues,
    setDesktopNavDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
    visibilityState,
    allowOpenParentLinks,
}: Props) => {
    const [isButtonClicked, setIsButtonClicked] = React.useState(false);


    const openDrawer = () => {
        isCompactHeaderFlyout
            ? setDesktopFlyoutDrawerIsOpen({ isOpen: true })
            : setDesktopNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const handleButtonClick = () => {
        setIsButtonClicked(true);
        openDrawer();
    };

    const closeDrawer = () => {
        if (isCompactHeaderFlyout) {
            setDesktopFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setDesktopFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }
        setIsButtonClicked(false);
        setDesktopNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setDesktopNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };



    const navigationModeChangeHandler = (navigationMode: "Storefront" | "Vmi") => {
        setCookie(NavigationModeCookieName, navigationMode);
        if (navigationMode === "Vmi") {
            vmiDashboardPageUrl && history.push(vmiDashboardPageUrl);
        } else if (cart?.status === "AlternateCart") {
            removeCart({
                cartId: API_URL_CURRENT_FRAGMENT,
                isAlternateCart: true,
                onSuccess: () => {
                    homePageUrl && history.push(homePageUrl);
                },
            });
        } else {
            homePageUrl && history.push(homePageUrl);
        }
    };

    const currentPageUrl = currentLocation.pathname;
    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;
    const { width } = useWindowSize();

    const drawerContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isOpen && drawerContentRef.current && !drawerContentRef.current.contains(event.target as Node)) {
                closeDrawer();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    return (
        <>
            <Button
                className={`menu-trigger-button btn btn-primary-dark ${isButtonClicked ? "clicked" : ""}`}
                onClick={handleButtonClick}
                {...styles.menuTriggerButton}
                data-test-selector="expandMobileMenu"
            >
                {translate("Browse Products")}
            </Button>
            {width !== null && width > 992 && (
                <Drawer
                    className="drawer"
                    draggable
                    position="right"
                    {...styles.drawer}
                    isOpen={isOpen}
                    headline={translate("Browse Products")}
                    handleClose={closeDrawer}
                    contentLabel="menu drawer"
                    data-test-selector="drawerSection"
                    rootElementSelector=".main-header"
                >
                    <div ref={drawerContentRef}>
                        <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                            {/* covers MainNavigation functionality */}

                            {!displayVmiNavigation &&
                                links.map((link: any, index) => {
                                    if (
                                        !!link.childrenType &&
                                        link.children &&
                                        link.children.filter((o: any) => !o.excludeFromNavigation).length > 0
                                    ) {
                                        const href =
                                            allowOpenParentLinks && link.url && link.url !== currentPageUrl
                                                ? link.url
                                                : undefined;
                                        const selectedIcon = (link?.linkIcon ?? "none") as keyof typeof iconMap;
                                        const icon = iconMap[selectedIcon];
                                        return (
                                            <PanelMenu
                                                className="panel-menu"
                                                key={index}
                                                currentUrl={currentPageUrl}
                                                closeOverlay={closeDrawer}
                                                parentCategoryName={link.title}
                                                parentCategoryUrl={link.urk}
                                                panelTrigger={
                                                    <PanelRow
                                                        className="main-navigation-row"
                                                        href={href}
                                                        isCurrent={link.url === currentPageUrl}
                                                        hasChildren={true}
                                                        allowOpenParentLinks={allowOpenParentLinks}
                                                        {...styles.mainNavigationRow}
                                                    >
                                                        <Icon src={icon} size={24} />
                                                        <Typography
                                                            className="main-navigation-row-text"
                                                            {...styles.mainNavigationRowTypography}
                                                        >
                                                            {link.title}
                                                        </Typography>
                                                    </PanelRow>
                                                }
                                                menuItems={link.children}
                                                maxDepth={link.maxDepth || 3}
                                                layer={0}
                                                allowOpenParentLinks={allowOpenParentLinks}
                                                {...styles.panelMenu}
                                            />
                                        );
                                    }
                                    return (
                                        <PanelRow
                                            key={index}
                                            href={link.url}
                                            target={link.openInNewWindow ? "_blank" : ""}
                                            className="main-navigation-row"
                                            onClick={closeDrawer}
                                            {...styles.mainNavigationRow}
                                        >
                                            <Typography
                                                className="main-navigation-row-text"
                                                {...styles.mainNavigationRowTypography}
                                            >
                                                {link.title}
                                            </Typography>
                                        </PanelRow>
                                    );
                                })}

                            {displayVmiNavigation &&
                                vmiPageLinks &&
                                vmiPageLinks.map(
                                    link =>
                                        link && (
                                            <PanelRow
                                                className="main-navigation-row"
                                                key={link.id}
                                                isCurrent={currentPageUrl === link.url}
                                                onClick={closeDrawer}
                                                href={link.url}
                                                {...styles.mainNavigationRow}
                                            >
                                                <Typography
                                                    className="main-navigation-row-text"
                                                    {...styles.mainNavigationRowTypography}
                                                >
                                                    {link.title}
                                                </Typography>
                                            </PanelRow>
                                        ),
                                )}
                        </StyledSection>

                        <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                            {/* <StyledWrapper className="currency-selector-wrapper" {...styles.currencySelectorWrapper}>
                                <SelectorMenu
                                    options={currencies?.map(c => {
                                        return {
                                            title: c.currencyCode.toUpperCase(),
                                            clickableProps: {
                                                onClick: () => {
                                                    setCurrency({ currencyId: c.id });
                                                    closeDrawer();
                                                },
                                            },
                                        };
                                    })}
                                    closeModal={closeDrawer}
                                    currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                                    currentOptionIcon={
                                        <Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>
                                    }
                                />
                            </StyledWrapper> */}

                            {displayModeSwitch && (
                                <SelectorMenu
                                    options={[
                                        {
                                            title: translate("Storefront"),
                                            clickableProps: {
                                                onClick: () => {
                                                    navigationModeChangeHandler("Storefront");
                                                    closeDrawer();
                                                },
                                            },
                                        },
                                        {
                                            title: translate("Vendor Managed Inventory"),
                                            clickableProps: {
                                                onClick: () => {
                                                    navigationModeChangeHandler("Vmi");
                                                    closeDrawer();
                                                },
                                            },
                                        },
                                    ]}
                                    closeModal={closeDrawer}
                                    currentOption={
                                        currentMode === "Vmi"
                                            ? translate("Vendor Managed Inventory")
                                            : translate("Storefront")
                                    }
                                />
                            )}
                            {displayPricingAndInventoryToggles && (
                                <>
                                    {(!isCompactHeaderFlyout || showFlyoutInventoryToggle) && (
                                        <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                                    )}
                                    {(!isCompactHeaderFlyout || showFlyoutPricingToggle) && (
                                        <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                                    )}
                                </>
                            )}
                        </StyledSection>
                    </div>
                </Drawer>
            )}
        </>
    );
};


interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren className="logo-links-row" {...styles.logoLinks}>
            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    className="logo-link-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            className="panel-menu"
            currentUrl={currentOption}
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawer)));
